import { createFileRoute, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth.js';

interface LoginSearch {
  redirect?: string;
  error?: string;
}

/**
 * @graph-node {Page}
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business ログインページ。Google OAuth認証ボタン・エラー表示・リダイレクト先保存
 * @graph-connects useAuth [calls] 認証フック呼び出し
 */
export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
    error: typeof search.error === 'string' ? search.error : undefined,
  }),
});

/** @graph-connects none */
function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const { redirect } = useSearch({ from: '/login' });

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="bg-mesh">
        <div className="bg-mesh-extra" />
      </div>

      {/* うっすらとしたblurグラデーション球体 */}
      <div
        className="fixed w-[400px] h-[400px] rounded-full opacity-[0.07] blur-[80px] pointer-events-none"
        style={{
          top: '15%',
          left: '10%',
          background: 'radial-gradient(circle, var(--color-accent-primary), transparent 70%)',
        }}
      />
      <div
        className="fixed w-[350px] h-[350px] rounded-full opacity-[0.05] blur-[80px] pointer-events-none"
        style={{
          bottom: '10%',
          right: '15%',
          background: 'radial-gradient(circle, #8b5cf6, transparent 70%)',
        }}
      />
      <div
        className="fixed w-[250px] h-[250px] rounded-full opacity-[0.04] blur-[60px] pointer-events-none"
        style={{
          top: '60%',
          left: '60%',
          background: 'radial-gradient(circle, #06b6d4, transparent 70%)',
        }}
      />

      <div className="glass text-center max-w-sm w-full mx-4 p-8 rounded-2xl border border-[var(--color-border)] shadow-[0_8px_40px_rgba(0,0,0,0.08)]">
        {/* ロゴ */}
        <div className="mb-6 flex justify-center">
          <div className="w-24 h-24 rounded-2xl card flex items-center justify-center">
            <img src="/favicon.svg" alt="UX Mirror" className="w-16 h-16" />
          </div>
        </div>

        <h1 className="text-2xl font-bold text-[var(--color-text-primary)] mb-1">
          {t('login.title')}
        </h1>
        <p className="text-sm text-[var(--color-text-muted)] mb-8">{t('login.subtitle')}</p>

        <button
          onClick={() => {
            if (redirect) {
              sessionStorage.setItem('ux_mirror_redirect', redirect);
            }
            login();
          }}
          className="w-full px-4 py-3 bg-[var(--color-accent-primary)] text-white rounded-xl font-medium text-sm hover:bg-[var(--color-accent-hover)] transition-all hover:shadow-[0_4px_20px_rgba(20,184,166,0.3)]"
        >
          <span className="flex items-center justify-center gap-2">
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {t('login.loginWith')}
          </span>
        </button>
      </div>
    </div>
  );
}
